'use client';

import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { BlogCardSkeleton } from './blog-cart-skeleton';
import { BlogCard } from './blog-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { BlogsListCarousel } from './blogs-list-carousel';

const ALL_CATEGORIES_TAB = 'all';

export function BlogsList({ categories, locale }: { categories: BlogCategory[]; locale: string }) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 8,
          order: 'desc',
          orderBy: 'created_at',
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = blogsQuery?.isLoading;

  return (
    <section
      className={cn('custom-section', 'pb-12 pr-0 pt-6 sm:pb-16 sm:pr-4 sm:pt-8 md:pb-20 lg:pb-24')}
    >
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn('custom-container', 'flex flex-col items-center justify-center gap-12')}
      >
        <BlogsListCarousel blogs={blogs.slice(0, 4)} />
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full flex-nowrap items-center justify-start gap-2 overflow-scroll rounded-none bg-transparent py-0 md:justify-center lg:gap-2',
          )}
        >
          <TabsTrigger
            value={ALL_CATEGORIES_TAB}
            className={cn(
              'flex h-12 w-fit items-center justify-center whitespace-nowrap border border-border text-center text-sm font-semibold tracking-tight sm:px-5 lg:px-6 lg:text-base',
              'ea-product-tabs__tabs-trigger',
            )}
          >
            {t('text2000')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              value={String(category.id)}
              key={category.id}
              className={cn(
                'flex h-12 w-fit items-center justify-center whitespace-nowrap border border-border text-center text-sm font-semibold tracking-tight sm:px-5 lg:px-6 lg:text-base',
                'ea-product-tabs__tabs-trigger',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value={activeTab}
          className="grid w-full grid-cols-1 gap-6 pr-5 sm:pr-0 md:grid-cols-2"
        >
          {blogsQuery.error && (
            <Paragraph className="col-span-1 pt-8 text-center md:col-span-2">
              {t('text2364')}
            </Paragraph>
          )}
          {isLoading ? (
            [...Array(6)].map((_, index) => <BlogCardSkeleton key={index} />)
          ) : blogs.length > 0 ? (
            <>
              {blogs.slice(1).map(blog => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </>
          ) : (
            <Paragraph
              color="muted-foreground"
              size="lg"
              className="col-span-1 text-center md:col-span-2"
            >
              {t('text2376')}
            </Paragraph>
          )}
        </TabsContent>
        {blogsQuery.hasNextPage && (
          <div className="flex w-full items-center justify-center pr-4 md:pr-0">
            <Button
              onClick={() => blogsQuery.fetchNextPage()}
              variant={'outline'}
              className="w-full sm:w-fit"
            >
              {t('text338')}
            </Button>
          </div>
        )}
      </Tabs>
    </section>
  );
}
